import { useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAdmobApps, importAdmobApp, getImports, getFirebaseAccounts, getFirebaseOrgs, registerImportFirebase,
  firebaseChoiceToInput, type FirebaseChoice,
  downloadGoogleServices, generateImportPrivacyPolicy, listSkeletons,
  suggestBundle, generateIcon, createSkeleton, generateDescriptions,
  listManualAdmob, createManualAdmob, updateManualAdmob, deleteManualAdmob,
  type AdmobPickerApp, type ImportResult, type Skeleton, type CreateSkeletonResult,
  type ManualAdmobApp, type ManualAdmobInput,
} from '@/api/apps-setup';
import { ApiError } from '@/api/client';
import { androidIconPackZip, icon512Png, saveBlob } from '@/lib/icon-export';
import { Select } from '@/components/Select';
import CreateScreenshots from './apps-setup/CreateScreenshots';
import { AppWizardModal, type AppWizardDraft } from './apps-setup/AppWizardModal';
import GenerateTab from './apps-setup/GenerateTab';
import BuildTab from './apps-setup/BuildTab';
import ShareTab from './apps-setup/ShareTab';
import ReposSetupTab from './apps-setup/ReposSetupTab';
import { FirebasePicker, PROVISION_STEP_LABEL } from './apps-setup/FirebasePicker';
import { useAuthStore } from '@/store/auth';
import { getAppOwners } from '@/api/apps';
import { retryProvision } from '@/api/firebase';

function KeyRow({ label, value }: { label: string; value: string | null }) {
  if (!value) return (
    <div className="flex justify-between text-sm py-0.5">
      <span className="text-content-subtle">{label}</span>
      <span className="text-content-subtle italic">—</span>
    </div>
  );
  return (
    <div className="flex items-start justify-between gap-2 text-sm py-0.5">
      <span className="text-content-subtle shrink-0">{label}</span>
      <span className="flex items-start gap-2 min-w-0">
        <code className="text-content break-all text-right">{value}</code>
        <button
          type="button"
          onClick={() => navigator.clipboard.writeText(value)}
          className="text-primary hover:underline shrink-0"
        >
          copy
        </button>
      </span>
    </div>
  );
}

function CopyText({ label, value, mono, multiline }: {
  label: string; value: string | null; mono?: boolean; multiline?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-content-subtle text-xs uppercase tracking-wide">{label}</span>
        {value && (
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            }}
            className="text-primary hover:underline text-xs shrink-0"
          >
            {copied ? 'copied' : 'copy'}
          </button>
        )}
      </div>
      {value ? (
        <p className={`text-content text-sm whitespace-pre-wrap break-words ${mono ? 'font-mono' : ''} ${multiline ? 'max-h-48 overflow-y-auto rounded-md bg-surface-muted p-2' : ''}`}>
          {value}
        </p>
      ) : (
        <p className="text-content-subtle text-sm italic">Not set — create it on the Create tab.</p>
      )}
    </div>
  );
}

// Marketing assets (icon + name + short/full description) for a Meta ad
// campaign. Data comes from the linked app row created on the Create tab.
function MetaPanel({ name, bundleId, iconUrl, shortDescription, fullDescription }: {
  name: string; bundleId: string | null; iconUrl: string | null;
  shortDescription: string | null; fullDescription: string | null;
}) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  function slug() {
    const s = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return s || 'app-icon';
  }

  async function downloadIcon() {
    if (!iconUrl) return;
    setError('');
    setDownloading(true);
    try {
      const png = await icon512Png(iconUrl);
      saveBlob(png, `${slug()}-icon-512.png`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="pt-2 mt-2 border-t border-border/50 space-y-3">
      <div className="flex items-center gap-3">
        {iconUrl ? (
          <img src={iconUrl} alt="app icon" crossOrigin="anonymous" className="w-16 h-16 rounded-xl object-cover border border-border shrink-0" />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-surface-muted border border-border flex items-center justify-center text-content-subtle text-xs shrink-0">no icon</div>
        )}
        <button
          type="button"
          disabled={!iconUrl || downloading}
          onClick={downloadIcon}
          className="px-2 py-1 text-xs rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
        >
          {downloading ? 'Downloading…' : 'Download icon'}
        </button>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
      <CopyText label="Name" value={name || null} />
      <CopyText label="Bundle" value={bundleId} />
      <CopyText label="Short description" value={shortDescription} />
      <CopyText label="Full description" value={fullDescription} multiline />
    </div>
  );
}

// String-valued fields of a key set, editable through the shared text-input handler.
type ManualTextKey =
  | 'play_console' | 'admob_app_id' | 'display_name' | 'bundle_id'
  | 'banner_unit_id' | 'interstitial_unit_id' | 'rewarded_unit_id';

const EMPTY_MANUAL: ManualAdmobInput = {
  play_console: '', admob_app_id: '', display_name: '', platform: 'android',
  bundle_id: '', banner_unit_id: '', interstitial_unit_id: '', rewarded_unit_id: '',
};

// The hand-maintained AdMob key-set pool: create / edit / delete entries that
// the picker below connects to apps. Replaces the old AdMob OAuth sync.
function ManualKeysSection({ onChanged }: { onChanged: () => void }) {
  const qc = useQueryClient();
  const pool = useQuery({ queryKey: ['admob-manual'], queryFn: listManualAdmob });
  const [form, setForm] = useState<ManualAdmobInput>(EMPTY_MANUAL);
  const [editingId, setEditingId] = useState<number | null>(null);

  function refresh() {
    qc.invalidateQueries({ queryKey: ['admob-manual'] });
    onChanged();
  }
  function reset() { setForm(EMPTY_MANUAL); setEditingId(null); }

  const save = useMutation({
    mutationFn: async () => {
      if (editingId) await updateManualAdmob(editingId, form);
      else await createManualAdmob(form);
    },
    onSuccess: () => { reset(); refresh(); },
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteManualAdmob(id),
    onSuccess: () => { refresh(); },
  });

  function edit(m: ManualAdmobApp) {
    setEditingId(m.id);
    setForm({
      play_console: m.play_console ?? '',
      admob_app_id: m.admob_app_id,
      display_name: m.display_name ?? '',
      platform: m.platform,
      bundle_id: m.bundle_id ?? '',
      banner_unit_id: m.banner_unit_id ?? '',
      interstitial_unit_id: m.interstitial_unit_id ?? '',
      rewarded_unit_id: m.rewarded_unit_id ?? '',
    });
  }

  const set = (k: ManualTextKey) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputClass = 'w-full px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content';

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
      <div>
        <h2 className="font-medium text-content">AdMob keys</h2>
        <p className="text-content-subtle text-xs mt-0.5">Maintained by hand. Add one entry per AdMob app, then connect it to an app below.</p>
      </div>

      {/* Create / edit form */}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Play Console</span>
          <input className={inputClass} placeholder="console name" value={form.play_console ?? ''} onChange={set('play_console')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">AdMob App ID *</span>
          <input className={inputClass} placeholder="ca-app-pub-…~…" value={form.admob_app_id} onChange={set('admob_app_id')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Display name</span>
          <input className={inputClass} placeholder="z2" value={form.display_name ?? ''} onChange={set('display_name')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Platform</span>
          <Select
            value={form.platform}
            onChange={(v) => setForm((f) => ({ ...f, platform: v === 'ios' ? 'ios' : 'android' }))}
            options={[{ value: 'android', label: 'Android' }, { value: 'ios', label: 'iOS' }]}
            className="w-full"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Banner unit</span>
          <input className={inputClass} placeholder="ca-app-pub-…/…" value={form.banner_unit_id ?? ''} onChange={set('banner_unit_id')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Interstitial unit</span>
          <input className={inputClass} placeholder="ca-app-pub-…/…" value={form.interstitial_unit_id ?? ''} onChange={set('interstitial_unit_id')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Rewarded unit</span>
          <input className={inputClass} placeholder="ca-app-pub-…/…" value={form.rewarded_unit_id ?? ''} onChange={set('rewarded_unit_id')} />
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!form.admob_app_id.trim() || save.isPending}
          onClick={() => save.mutate()}
          className="px-4 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : editingId ? 'Save changes' : 'Add keys'}
        </button>
        {editingId != null && (
          <button type="button" onClick={reset} className="px-3 py-1.5 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted">
            Cancel
          </button>
        )}
        {save.error && <p className="text-red-500 text-sm">{(save.error as ApiError).message}</p>}
      </div>

      {/* Pool list */}
      {(pool.data?.length ?? 0) === 0 ? (
        <p className="text-content-subtle text-sm">No AdMob keys yet.</p>
      ) : (
        <div className="space-y-2">
          {pool.data!.map((m) => (
            <div key={m.id} className="flex items-start justify-between gap-3 border border-border rounded-md p-2.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-content font-medium truncate">{m.display_name || m.admob_app_id}</span>
                  <span className="text-[10px] uppercase tracking-wide text-content-subtle px-1.5 py-0.5 rounded bg-surface-muted">{m.platform}</span>
                  {m.connected ? (
                    <span className="text-[10px] uppercase tracking-wide text-green-600 px-1.5 py-0.5 rounded bg-green-500/10">
                      connected{m.connected_app_name ? ` · ${m.connected_app_name}` : ''}
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wide text-content-subtle px-1.5 py-0.5 rounded bg-surface-muted">unconnected</span>
                  )}
                </div>
                <div className="text-content-subtle text-xs mt-0.5 flex flex-wrap items-center gap-x-1.5">
                  {m.play_console && <><span>{m.play_console}</span><span>·</span></>}
                  <code className="truncate">{m.admob_app_id}</code>
                  {m.bundle_id && <><span>·</span><span>{m.bundle_id}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={() => edit(m)} className="text-primary hover:underline text-xs">Edit</button>
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => { if (confirm('Delete this AdMob key set?')) remove.mutate(m.id); }}
                  className="text-red-500 hover:underline text-xs disabled:opacity-40"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {remove.error && <p className="text-red-500 text-sm">{(remove.error as ApiError).message}</p>}
    </section>
  );
}

// The subset of the app row POST /import returns alongside its 409 when the
// bundle is already taken.
interface ExistingApp { id: string; name: string; bundle_id: string; status: string }

export default function AppsSetup() {
  const qc = useQueryClient();
  const admin = useAuthStore((s) => s.admin);
  const isTechnical = admin?.role === 'super_admin' || !!admin?.addons?.includes('developer');
  const isClient = useAuthStore((s) => s.isClient());

  const apps = useQuery({ queryKey: ['admob-apps'], queryFn: getAdmobApps, enabled: isTechnical });
  const imports = useQuery({
    queryKey: ['admob-imports'],
    queryFn: getImports,
    // AdMob imports are our pipeline; a client has no access to them.
    enabled: !isClient,
    // A Firebase project takes a minute or two to provision in the background;
    // keep the history fresh while any run is still going.
    refetchInterval: (q) => (q.state.data ?? []).some((r) =>
      r.firebase_provision_id && r.firebase_provision_state !== 'ready' && !r.firebase_provision_error) ? 5_000 : false,
  });
  const firebaseAccounts = useQuery({ queryKey: ['firebase-accounts'], queryFn: getFirebaseAccounts, enabled: isTechnical });
  const firebaseOrgs = useQuery({ queryKey: ['firebase-orgs'], queryFn: getFirebaseOrgs, enabled: isTechnical });
  const fbAccounts = firebaseAccounts.data ?? [];
  const fbOrgs = firebaseOrgs.data ?? [];
  const hasFirebaseChoice = fbAccounts.length > 0 || fbOrgs.length > 0;
  const skeletons = useQuery({ queryKey: ['apps-setup-skeletons'], queryFn: listSkeletons });
  const owners = useQuery({
    queryKey: ['users', 'app-owners'],
    queryFn: getAppOwners,
    enabled: isTechnical,
  });

  const [result, setResult] = useState<ImportResult | null>(null);
  const [bundleFor, setBundleFor] = useState<Record<string, string>>({});
  const [nameFor, setNameFor] = useState<Record<string, string>>({});
  const [fbFor, setFbFor] = useState<Record<string, FirebaseChoice | ''>>({});
  const [fbForHistory, setFbForHistory] = useState<Record<number, FirebaseChoice | ''>>({});
  const [emailFor, setEmailFor] = useState<Record<string, string>>({});
  const [emailForHistory, setEmailForHistory] = useState<Record<number, string>>({});
  const [linkTarget, setLinkTarget] = useState<Record<string, string>>({}); // admob appId -> skeleton app_id
  const [ownerFor, setOwnerFor] = useState<Record<string, string>>({});
  const [metaOpen, setMetaOpen] = useState<Record<number, boolean>>({}); // import id -> Meta panel expanded
  // A bundle already carried by a live app comes back as 409 with that app
  // attached. Connect only lists paused, never-imported apps, so an app that is
  // already running can otherwise be connected by no route at all. Keep the app
  // the backend named, keyed by AdMob app id, and offer to link to it.
  const [existingFor, setExistingFor] = useState<Record<string, ExistingApp>>({});

  const doImport = useMutation({
    mutationFn: importAdmobApp,
    onSuccess: (r, input) => {
      setResult(r);
      setExistingFor((m) => { const { [input.admob_app_id]: _drop, ...rest } = m; return rest; });
      qc.invalidateQueries({ queryKey: ['admob-apps'] });
      qc.invalidateQueries({ queryKey: ['admob-imports'] });
      qc.invalidateQueries({ queryKey: ['apps-setup-skeletons'] });
    },
    onError: (e, input) => {
      const existing = (e as ApiError).status === 409
        ? ((e as ApiError).data as { data?: { existing?: ExistingApp } } | undefined)?.data?.existing
        : undefined;
      if (existing) setExistingFor((m) => ({ ...m, [input.admob_app_id]: existing }));
    },
  });
  const doRegister = useMutation({
    mutationFn: ({ id, choice }: { id: number; choice: FirebaseChoice }) => registerImportFirebase(id, choice),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admob-imports'] }); },
  });
  const doRetryProvision = useMutation({
    mutationFn: (id: number) => retryProvision(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admob-imports'] }); },
  });
  const doGenPolicy = useMutation({
    mutationFn: ({ id, email }: { id: number; email: string }) => generateImportPrivacyPolicy(id, email),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admob-imports'] }); },
  });

  function importApp(a: AdmobPickerApp, linkExisting = false) {
    const skel = skeletons.data?.find((s) => s.app_id === linkTarget[a.appId]);
    const bundle = skel?.bundle_id || a.packageName || bundleFor[a.appId] || '';
    const name = (skel?.name ?? nameFor[a.appId] ?? '').trim();
    const email = (emailFor[a.appId] ?? '').trim();
    const fb = fbFor[a.appId];
    const appId = linkTarget[a.appId] || undefined;
    doImport.mutate({
      admob_app_id: a.appId,
      app_id: appId,
      name: name || undefined,
      bundle_id: bundle || undefined,
      ...firebaseChoiceToInput(fb),
      link_existing: linkExisting || undefined,
      email: email || undefined,
      owner_admin_id: skel?.owner_admin_id ? undefined : (ownerFor[a.appId] || undefined),
    });
  }

  // ?tab=build&app=<id> opens the Build tab on an app — Update Batches links here.
  const [searchParams] = useSearchParams();
  const TABS = ['create', 'connect', 'generate', 'build', 'share', 'repos'] as const;
  type Tab = (typeof TABS)[number];
  const initialTab = searchParams.get('tab') as Tab | null;
  const technicalOnly = (t: Tab) => t === 'build' || t === 'repos';
  const [tab, setTab] = useState<Tab>(initialTab && TABS.includes(initialTab) && (!technicalOnly(initialTab) || isTechnical) ? initialTab : 'create');

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-content">Apps Setup</h1>
        <p className="text-content-muted text-sm mt-1">
          {isClient
            ? 'Create the store listing for your app — name, icon, descriptions, screenshots, translations — upload your build and submit it for publishing.'
            : 'Marketers create app skeletons with icons, bundles, and descriptions. Developers maintain AdMob keys by hand, connect them to apps, and set up Firebase and Privacy Policy.'}
        </p>
      </div>

      <div className="flex gap-2 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('create')}
          className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === 'create' ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}
        >
          Create
        </button>
        {isTechnical && <button
          type="button"
          onClick={() => setTab('connect')}
          className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === 'connect' ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}
        >
          Connect
        </button>}
        {isTechnical && (
          <button
            type="button"
            onClick={() => setTab('build')}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === 'build' ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}
          >
            Build
          </button>
        )}
        <button
          type="button"
          onClick={() => setTab('generate')}
          className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === 'generate' ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}
        >
          Generate
        </button>
        <button
          type="button"
          onClick={() => setTab('share')}
          className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === 'share' ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}
        >
          {isClient ? 'Listing & publishing' : 'Share'}
        </button>
        {isTechnical && (
          <button
            type="button"
            onClick={() => setTab('repos')}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === 'repos' ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}
          >
            Repos Setup
          </button>
        )}
      </div>

      {tab === 'create' && <CreateTab isTechnical={isTechnical} isClient={isClient} onCreated={() => qc.invalidateQueries({ queryKey: ['apps-setup-skeletons'] })} />}
      {isTechnical && tab === 'connect' && (
        <div className="space-y-8">

      {/* AdMob keys — maintained by hand (replaces the old OAuth sync) */}
      <ManualKeysSection onChanged={() => qc.invalidateQueries({ queryKey: ['admob-apps'] })} />

      {/* Import result */}
      {result && (
        <section className="bg-surface border border-primary rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-medium text-content">Imported "{result.name}"</h2>
            <button type="button" onClick={() => setResult(null)} className="text-content-subtle hover:text-content text-sm">dismiss</button>
          </div>
          <KeyRow label="Our API key" value={result.api_key} />
          <KeyRow label="AdMob App ID" value={result.admob_app_id} />
          <KeyRow label="Banner" value={result.banner_unit_id} />
          <KeyRow label="Interstitial" value={result.interstitial_unit_id} />
          <KeyRow label="Rewarded" value={result.rewarded_unit_id} />
          {result.firebase_error ? (
            <p className="text-red-500 text-sm mt-2">Firebase: {result.firebase_error}</p>
          ) : result.firebase_project_id ? (
            <p className="text-green-600 text-sm mt-2">Firebase project: {result.firebase_project_id}</p>
          ) : result.firebase_provision_id ? (
            <p className="text-content-muted text-sm mt-2">Firebase: creating a new project in the background — follow it in the history below.</p>
          ) : null}
          {result.privacy_policy_error ? (
            <p className="text-red-500 text-sm mt-2">Privacy policy: {result.privacy_policy_error}</p>
          ) : result.privacy_policy_url ? (
            <p className="text-green-600 text-sm mt-2">
              Privacy policy: <a href={result.privacy_policy_url} target="_blank" rel="noreferrer" className="underline">{result.privacy_policy_url}</a>
            </p>
          ) : null}
        </section>
      )}

      {/* Picker: connect an un-connected key set to an app */}
      {isTechnical && (
        <section>
          {(skeletons.data?.length ?? 0) > 0 && (
            <div className="mb-3 text-sm text-content-muted">
              Skeletons without AdMob: {skeletons.data!.map((s: Skeleton) => s.name).join(', ')}
            </div>
          )}
          <h2 className="font-medium text-content mb-2">Connect keys to an app</h2>
          {apps.isLoading ? (
            <p className="text-content-subtle text-sm">Loading AdMob keys…</p>
          ) : apps.error ? (
            <p className="text-red-500 text-sm">{(apps.error as ApiError).message}</p>
          ) : (apps.data?.length ?? 0) === 0 ? (
            <p className="text-content-subtle text-sm">No unconnected AdMob keys — add one above.</p>
          ) : (
            <div className="space-y-3">
              {apps.data!.map((a) => {
                const skel = skeletons.data?.find((s) => s.app_id === linkTarget[a.appId]);
                const nameValue = skel ? skel.name : (nameFor[a.appId] ?? '');
                const bundleLocked = !!skel || !!a.packageName;
                const bundleValue = skel ? skel.bundle_id : (a.packageName || bundleFor[a.appId] || '');
                const inputClass = 'w-full px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content read-only:opacity-60 read-only:cursor-not-allowed';
                return (
                  <div key={a.appId} className="bg-surface border border-border rounded-lg p-4 space-y-3">
                    {/* App info */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-content font-medium truncate">{a.displayName || a.appId}</div>
                        <div className="text-content-subtle text-xs mt-0.5 flex flex-wrap items-center gap-x-1.5">
                          <span>{a.packageName || 'no store link'}</span>
                          <span>·</span>
                          <span>{a.adUnitCount} ad units</span>
                          <span>·</span>
                          <code className="truncate">{a.appId}</code>
                        </div>
                      </div>
                      <span className="shrink-0 text-[10px] uppercase tracking-wide text-content-subtle px-1.5 py-0.5 rounded bg-surface-muted">
                        {a.platform}
                      </span>
                    </div>

                    {/* Fields */}
                    <div className="flex flex-wrap items-end gap-2">
                      {(skeletons.data?.length ?? 0) > 0 && (
                        <label className="flex flex-col gap-1 min-w-[12rem] flex-1">
                          <span className="text-content-subtle text-xs">App</span>
                          <Select
                            value={linkTarget[a.appId] ?? ''}
                            onChange={(v) => setLinkTarget((m) => ({ ...m, [a.appId]: v }))}
                            options={[
                              { value: '', label: 'New app' },
                              ...skeletons.data!.map((s) => ({ value: s.app_id, label: `${s.name} · ${s.bundle_id}` })),
                            ]}
                            className="w-full"
                          />
                        </label>
                      )}
                      <label className="flex flex-col gap-1 min-w-[10rem] flex-1">
                        <span className="text-content-subtle text-xs">Name</span>
                        <input
                          placeholder={a.displayName || 'name'}
                          value={nameValue}
                          readOnly={!!skel}
                          onChange={(e) => setNameFor((m) => ({ ...m, [a.appId]: e.target.value }))}
                          className={inputClass}
                        />
                      </label>
                      {(!skel || !skel.owner_admin_id) && (
                        <label className="flex flex-col gap-1 min-w-[12rem] flex-1">
                          <span className="text-content-subtle text-xs">ASO owner</span>
                          <Select
                            value={ownerFor[a.appId] ?? ''}
                            onChange={(v) => setOwnerFor((m) => ({ ...m, [a.appId]: v }))}
                            options={[
                              { value: '', label: 'Select owner' },
                              ...(owners.data ?? [])
                                .map((u) => ({ value: u.id, label: u.name || u.email })),
                            ]}
                            className="w-full"
                          />
                        </label>
                      )}
                      <label className="flex flex-col gap-1 min-w-[10rem] flex-1">
                        <span className="text-content-subtle text-xs">Bundle id</span>
                        <input
                          placeholder="com.example.app"
                          value={bundleValue}
                          readOnly={bundleLocked}
                          onChange={(e) => {
                            setBundleFor((m) => ({ ...m, [a.appId]: e.target.value }));
                            // A different bundle is a different question; drop the
                            // answer the backend gave about the old one.
                            setExistingFor((m) => { const { [a.appId]: _drop, ...rest } = m; return rest; });
                          }}
                          className={inputClass}
                        />
                      </label>
                      <label className="flex flex-col gap-1 min-w-[12rem] flex-1">
                        <span className="text-content-subtle text-xs">Contact email (optional)</span>
                        <input
                          placeholder="contact email"
                          value={emailFor[a.appId] ?? ''}
                          onChange={(e) => setEmailFor((m) => ({ ...m, [a.appId]: e.target.value }))}
                          className={inputClass}
                        />
                      </label>
                      {a.platform === 'ANDROID' && hasFirebaseChoice && (
                        <FirebasePicker
                          accounts={fbAccounts}
                          orgs={fbOrgs}
                          value={fbFor[a.appId] ?? ''}
                          onChange={(v) => setFbFor((m) => ({ ...m, [a.appId]: v }))}
                        />
                      )}
                      <button
                        type="button"
                        disabled={doImport.isPending || (!bundleValue.trim() && !linkTarget[a.appId]) || ((!skel || !skel.owner_admin_id) && !ownerFor[a.appId])}
                        onClick={() => importApp(a)}
                        className="px-4 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
                      >
                        Import
                      </button>
                    </div>
                    {existingFor[a.appId] && (
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                        <p className="text-content">
                          App <span className="font-medium">{existingFor[a.appId].name}</span>{' '}
                          <span className="text-content-subtle">({existingFor[a.appId].bundle_id}, {existingFor[a.appId].status})</span>{' '}
                          already uses this bundle id. Connect only offers paused apps that were
                          never imported, so a running app has to be linked from here.
                        </p>
                        <p className="text-content-subtle text-xs mt-1">
                          Linking attaches these AdMob keys to that app. Its status is left as it is —
                          a live app keeps serving.
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            type="button"
                            disabled={doImport.isPending}
                            onClick={() => importApp(a, true)}
                            className="px-3 py-1 text-xs rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
                          >
                            Link to this app
                          </button>
                          <button
                            type="button"
                            onClick={() => setExistingFor((m) => { const { [a.appId]: _drop, ...rest } = m; return rest; })}
                            className="px-3 py-1 text-xs rounded-md border border-border text-content hover:bg-surface-muted"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {doImport.error && !existingFor[doImport.variables?.admob_app_id ?? ''] && (
            <p className="text-red-500 text-sm mt-2">{(doImport.error as ApiError).message}</p>
          )}
        </section>
      )}

      {/* History */}
      <section>
        <h2 className="font-medium text-content mb-2">Import history</h2>
        {(imports.data?.length ?? 0) === 0 ? (
          <p className="text-content-subtle text-sm">Nothing imported yet.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {imports.data!.map((r) => (
              <div key={r.id} className="bg-surface border border-border rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="min-w-0">
                    <span className="block text-content font-medium truncate">
                      {r.app_name || r.bundle_id}
                    </span>
                    {r.app_name && (
                      <span className="block text-content-subtle text-xs truncate" title={r.bundle_id}>{r.bundle_id}</span>
                    )}
                    {r.admob_app_display && (
                      <span className="block text-content-subtle text-xs truncate">AdMob: {r.admob_app_display}</span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setMetaOpen((m) => ({ ...m, [r.id]: !m[r.id] }))}
                      aria-expanded={!!metaOpen[r.id]}
                      className={`text-xs px-2 py-0.5 rounded-md border transition-colors ${
                        metaOpen[r.id]
                          ? 'bg-primary text-white border-primary'
                          : 'border-border text-content hover:bg-surface-muted'
                      }`}
                    >
                      Meta
                    </button>
                    <span className="text-[10px] uppercase tracking-wide text-content-subtle px-1.5 py-0.5 rounded bg-surface-muted">
                      {r.platform}
                    </span>
                  </span>
                </div>
                {metaOpen[r.id] && (
                  <MetaPanel
                    name={r.app_name || r.bundle_id}
                    bundleId={r.bundle_id}
                    iconUrl={r.icon_url}
                    shortDescription={r.short_description}
                    fullDescription={r.full_description}
                  />
                )}
                <KeyRow label="API key" value={r.api_key} />
                <KeyRow label="AdMob App ID" value={r.admob_app_id} />
                <KeyRow label="Banner" value={r.banner_unit_id} />
                <KeyRow label="Interstitial" value={r.interstitial_unit_id} />
                <KeyRow label="Rewarded" value={r.rewarded_unit_id} />
                {r.firebase_registered_at ? (
                  <div className="flex items-center justify-between gap-2 pt-2 mt-1 border-t border-border/50">
                    <span className="text-content-subtle text-xs truncate">Firebase: {r.firebase_project_id}</span>
                    <button
                      type="button"
                      onClick={() => downloadGoogleServices(r.id)}
                      className="shrink-0 px-2 py-1 text-xs rounded-md bg-primary text-white hover:opacity-90"
                    >
                      Download google-services.json
                    </button>
                  </div>
                ) : r.firebase_provision_id && r.firebase_provision_state !== 'ready' ? (
                  // A new project is on its way (or stuck): show where it is,
                  // and offer to resume from the last finished step.
                  <div className="pt-2 mt-1 border-t border-border/50 text-xs">
                    {r.firebase_provision_error ? (
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-red-500 break-words">
                          Firebase project {r.firebase_provision_project_id}: {r.firebase_provision_error}
                        </span>
                        <button
                          type="button"
                          disabled={doRetryProvision.isPending}
                          onClick={() => doRetryProvision.mutate(r.firebase_provision_id!)}
                          className="shrink-0 px-2 py-1 rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
                        >
                          Retry
                        </button>
                      </div>
                    ) : (
                      <span className="text-content-muted">
                        Creating Firebase project {r.firebase_provision_project_id}: {PROVISION_STEP_LABEL[r.firebase_provision_state ?? 'new']}…
                      </span>
                    )}
                  </div>
                ) : hasFirebaseChoice && r.platform === 'android' ? (
                  <div className="pt-2 mt-1 border-t border-border/50">
                    <div className="flex items-start gap-2">
                      <FirebasePicker
                        compact
                        accounts={fbAccounts}
                        orgs={fbOrgs}
                        value={fbForHistory[r.id] ?? ''}
                        onChange={(v) => setFbForHistory((m) => ({ ...m, [r.id]: v }))}
                      />
                      <button
                        type="button"
                        disabled={doRegister.isPending || !fbForHistory[r.id]}
                        onClick={() => doRegister.mutate({ id: r.id, choice: fbForHistory[r.id] as FirebaseChoice })}
                        className="shrink-0 px-2 py-1 text-xs rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
                      >
                        Register in Firebase
                      </button>
                    </div>
                    {/* Registration calls the Firebase API and can fail for reasons only
                        it knows (the project's app limit, a package the service account
                        may not touch). Without this line the click looked like it did
                        nothing at all. */}
                    {doRegister.error && doRegister.variables?.id === r.id && (
                      <p className="text-red-500 text-xs mt-1 break-words">
                        {(doRegister.error as ApiError).message}
                      </p>
                    )}
                  </div>
                ) : null}
                {r.privacy_policy_url ? (
                  <div className="flex items-center justify-between gap-2 pt-2 mt-1 border-t border-border/50">
                    <span className="text-content-subtle text-xs truncate">Privacy policy</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <a href={r.privacy_policy_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">Open</a>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard.writeText(r.privacy_policy_url!)}
                        className="text-primary hover:underline text-xs"
                      >
                        copy
                      </button>
                    </span>
                  </div>
                ) : (
                  <div className="pt-2 mt-1 border-t border-border/50">
                    <div className="flex items-center gap-2">
                      <input
                        placeholder="contact email"
                        value={emailForHistory[r.id] ?? ''}
                        onChange={(e) => setEmailForHistory((m) => ({ ...m, [r.id]: e.target.value }))}
                        className="flex-1 min-w-0 px-2 py-1 text-xs rounded-md border border-border bg-surface-muted text-content"
                      />
                      <button
                        type="button"
                        disabled={doGenPolicy.isPending || !(emailForHistory[r.id] ?? '').trim()}
                        onClick={() => doGenPolicy.mutate({ id: r.id, email: (emailForHistory[r.id] ?? '').trim() })}
                        className="shrink-0 px-2 py-1 text-xs rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
                      >
                        Generate privacy policy
                      </button>
                    </div>
                    {doGenPolicy.error && doGenPolicy.variables?.id === r.id && (
                      <p className="text-red-500 text-xs mt-1 break-words">
                        {(doGenPolicy.error as ApiError).message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
        </div>
      )}
      {tab === 'generate' && <GenerateTab />}
      {tab === 'share' && <ShareTab canAttachBuild={isTechnical} />}
      {isTechnical && tab === 'build' && <BuildTab onOpenShare={() => setTab('share')} onOpenRepos={() => setTab('repos')} />}
      {isTechnical && tab === 'repos' && <ReposSetupTab />}
    </div>
  );
}

function CreateTab({ isTechnical, isClient, onCreated }: { isTechnical: boolean; isClient: boolean; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [shortDesc, setShortDesc] = useState('');
  const [fullDesc, setFullDesc] = useState('');
  const [platform, setPlatform] = useState<'android' | 'ios'>('android');
  const [bundle, setBundle] = useState('');
  const [iconPrompt, setIconPrompt] = useState('');
  const [candidates, setCandidates] = useState<string[]>([]);
  const [chosenIcon, setChosenIcon] = useState<string>('');
  const [created, setCreated] = useState<CreateSkeletonResult | null>(null);
  const [exporting, setExporting] = useState<string>('');
  const [exportError, setExportError] = useState<string>('');
  const [ownerAdminId, setOwnerAdminId] = useState('');
  // MEDIA keys of the frames drawn below, saved with the app on create.
  const [shotKeys, setShotKeys] = useState<string[]>([]);
  // The base build stub picked in the screenshots block below — it also seeds
  // the app's build config (base repo + generated keystore) on create.
  const [stubId, setStubId] = useState('');
  // MEDIA key of the feature banner made in the wizard, saved with the app.
  const [featureKey, setFeatureKey] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  // Set only by the wizard draft: create stores the job's localizations with
  // the app as a listing package.
  const [wizardJobId, setWizardJobId] = useState('');

  // The AI wizard fills the same form the operator would fill by hand; the
  // create call itself is untouched.
  function applyWizardDraft(draft: AppWizardDraft) {
    setName(draft.name);
    setShortDesc(draft.short_description);
    setFullDesc(draft.full_description);
    setBundle(draft.bundle_id);
    if (draft.icon_url) {
      setCandidates([draft.icon_url]);
      setChosenIcon(draft.icon_url);
    }
    setShotKeys(draft.screenshot_keys);
    setFeatureKey(draft.feature_graphic_key);
    setStubId(draft.stub_id);
    setWizardJobId(draft.wizard_job_id);
    setWizardOpen(false);
  }
  const owners = useQuery({
    queryKey: ['admin-users', 'app-owners'],
    queryFn: getAppOwners,
    enabled: isTechnical,
  });

  const genBundle = useMutation({
    mutationFn: () => suggestBundle({ name, short_description: shortDesc, full_description: fullDesc }),
    onSuccess: (d) => setBundle(d.bundle_id),
  });
  const genDesc = useMutation({
    mutationFn: () => generateDescriptions({ name }),
    onSuccess: (d) => { setShortDesc(d.short_description); setFullDesc(d.full_description); },
  });
  const genIcon = useMutation({
    mutationFn: () => generateIcon({ prompt: iconPrompt, brand: name.trim() || undefined }),
    onSuccess: (d) => { setCandidates(d.candidates); setChosenIcon(''); },
  });
  const create = useMutation({
    mutationFn: () => createSkeleton({
      name, short_description: shortDesc, full_description: fullDesc,
      bundle_id: bundle, icon_url: chosenIcon || undefined, platform,
      owner_admin_id: isTechnical ? ownerAdminId : undefined,
      screenshot_keys: shotKeys.length > 0 ? shotKeys : undefined,
      feature_graphic_key: featureKey || undefined,
      base_repo_id: stubId ? Number(stubId) : undefined,
      wizard_job_id: wizardJobId || undefined,
    }),
    onSuccess: (r) => {
      setCreated(r);
      onCreated();
    },
  });

  function slug() {
    const s = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return s || 'app-icon';
  }

  async function downloadPack(url: string) {
    setExportError('');
    setExporting(url + ':pack');
    try {
      const zip = await androidIconPackZip(url);
      saveBlob(zip, `${slug()}-android-icons.zip`);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting('');
    }
  }

  async function download512(url: string) {
    setExportError('');
    setExporting(url + ':512');
    try {
      const png = await icon512Png(url);
      saveBlob(png, `${slug()}-icon-512.png`);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting('');
    }
  }

  const canCreate = name.trim().length > 0 && bundle.trim().length > 0 && (!isTechnical || !!ownerAdminId) && !create.isPending;

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-medium text-content">New app</h2>
        <button
          type="button"
          onClick={() => setWizardOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 3l1.3 4.1L17 9l-3.7 1.9L12 15l-1.3-4.1L7 9l3.7-1.9L12 3zM5 14l.8 2.2L8 17l-2.2.8L5 20l-.8-2.2L2 17l2.2-.8L5 14zM19 13l.8 2.2L22 16l-2.2.8L19 19l-.8-2.2L16 16l2.2-.8L19 13z" />
          </svg>
          App wizard
        </button>
      </div>
      <AppWizardModal
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onUseDraft={applyWizardDraft}
        isTechnical={isTechnical}
        ownerAdminId={ownerAdminId}
        owners={(owners.data ?? []).map((u) => ({ value: u.id, label: u.name ? `${u.name} · ${u.email}` : u.email }))}
        onOwnerChange={setOwnerAdminId}
        onCreated={(r) => { setCreated(r); onCreated(); }}
        onFinished={(r) => {
          // Rejoined after a reload the create result is gone; the finish
          // result carries everything the panel shows except the API key.
          setCreated((prev) => prev && prev.app_id === r.app_id
            ? { ...prev, ...r }
            : { api_key: '', platform: 'android', status: 'paused', ...r });
          setWizardOpen(false);
          onCreated();
        }}
      />

      {isTechnical && (
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">ASO owner</label>
          <Select
            value={ownerAdminId}
            options={(owners.data ?? []).map((u) => ({ value: u.id, label: u.name ? `${u.name} · ${u.email}` : u.email }))}
            onChange={setOwnerAdminId}
            placeholder="— select responsible ASO —"
            className="w-full"
          />
          <p className="mt-1 text-xs text-content-subtle">The owner receives access immediately; Firebase, Play and Tracker will inherit this app.</p>
        </div>
      )}

      <div className="space-y-2">
        <input
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
        />
        <input
          placeholder="Short description"
          value={shortDesc}
          onChange={(e) => setShortDesc(e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
        />
        <textarea
          placeholder="Full description"
          value={fullDesc}
          onChange={(e) => setFullDesc(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
        />
        {!isClient && (
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as 'android' | 'ios')}
            className="w-40 px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
          >
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </select>
        )}
        <button
          type="button"
          disabled={!name.trim() || genDesc.isPending}
          onClick={() => genDesc.mutate()}
          className="px-3 py-2 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
        >
          {genDesc.isPending ? 'Generating...' : 'Generate descriptions'}
        </button>
        {genDesc.error && <p className="text-red-500 text-sm">{(genDesc.error as ApiError).message}</p>}
      </div>

      <div className="flex items-center gap-2">
        <input
          placeholder="com.example.app"
          value={bundle}
          onChange={(e) => setBundle(e.target.value)}
          className="flex-1 px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
        />
        <button
          type="button"
          disabled={!name.trim() || genBundle.isPending}
          onClick={() => genBundle.mutate()}
          className="shrink-0 px-3 py-2 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
        >
          {genBundle.isPending ? 'Generating...' : 'Generate bundle'}
        </button>
      </div>
      {genBundle.error && <p className="text-red-500 text-sm">{(genBundle.error as ApiError).message}</p>}

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input
            placeholder="Icon prompt"
            value={iconPrompt}
            onChange={(e) => setIconPrompt(e.target.value)}
            className="flex-1 px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content"
          />
          <button
            type="button"
            disabled={!iconPrompt.trim() || genIcon.isPending}
            onClick={() => genIcon.mutate()}
            className="shrink-0 px-3 py-2 text-sm rounded-md border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40"
          >
            {genIcon.isPending ? 'Generating...' : 'Generate icon'}
          </button>
        </div>
        {genIcon.error && <p className="text-red-500 text-sm">{(genIcon.error as ApiError).message}</p>}
        {candidates.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {candidates.map((url) => (
              <div
                key={url}
                className={`border rounded-lg p-2 ${chosenIcon === url ? 'border-primary' : 'border-border'}`}
              >
                <img src={url} alt="icon candidate" crossOrigin="anonymous" className="w-full rounded-md aspect-square object-cover" />
                <div className="flex items-center justify-between mt-2">
                  <button
                    type="button"
                    onClick={() => setChosenIcon(url)}
                    className="text-xs text-primary hover:underline"
                  >
                    {chosenIcon === url ? 'Selected' : 'Select'}
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={exporting !== ''}
                      onClick={() => download512(url)}
                      className="text-xs text-content-muted hover:underline disabled:opacity-40"
                    >
                      {exporting === url + ':512' ? '...' : '512px'}
                    </button>
                    <button
                      type="button"
                      disabled={exporting !== ''}
                      onClick={() => downloadPack(url)}
                      className="text-xs text-content-muted hover:underline disabled:opacity-40"
                    >
                      {exporting === url + ':pack' ? '...' : 'Android pack'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {exportError && <p className="text-red-500 text-sm">{exportError}</p>}
      </div>

      <CreateScreenshots
        name={name}
        shortDesc={shortDesc}
        fullDesc={fullDesc}
        onKeysChange={setShotKeys}
        stubId={stubId}
        onStubChange={setStubId}
      />

      <button
        type="button"
        disabled={!canCreate}
        onClick={() => create.mutate()}
        className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
      >
        {create.isPending ? 'Creating...' : 'Create app'}
      </button>
      {create.error && <p className="text-red-500 text-sm">{(create.error as ApiError).message}</p>}

      {created && (
        <div className="bg-surface-muted border border-primary rounded-lg p-3 mt-2">
          <p className="text-content font-medium mb-1">Created "{created.name}"</p>
          <KeyRow label="App ID" value={created.app_id} />
          {created.api_key && <KeyRow label="Our API key" value={created.api_key} />}
          <KeyRow label="Bundle" value={created.bundle_id} />
          <p className="text-content-subtle text-xs mt-1">
            {isClient
              ? 'Next: open "Listing & publishing" to finish the listing, upload your AAB and submit it.'
              : `Status: ${created.status}. Add AdMob keys and connect them in the "Connect" tab.`}
            {created.screenshots_attached > 0
              && ` ${created.screenshots_attached} screenshot(s) saved with the app.`}
            {created.feature_graphic_attached && ' Feature banner saved with the app.'}
          </p>
          {!isClient && created.build && created.platform === 'android' && (
            <p className="text-content-subtle text-xs mt-1">
              Build: {created.build.base_repo_id != null ? 'base repo seeded' : 'no base repo picked'},{' '}
              {created.build.keystore_generated ? 'keystore generated' : 'keystore not generated'}.
            </p>
          )}
          {created.listing_package && (
            <p className="text-content-subtle text-xs mt-1">
              Listing package: {created.listing_package.locales} locale(s), {created.listing_package.screenshots} localized screenshot(s) — import it from the Play page once the app is linked.
            </p>
          )}
          {created.template && (
            <div className="mt-2 pt-2 border-t border-border">
              <p className="text-content text-xs">
                Template: {created.template.tracker_campaign_ids.length} tracker campaign(s),{' '}
                {created.template.ad_campaign_ids.length} ad campaign(s), {created.template.ads_count} creative(s). Nothing serves yet: the app stays paused and the force offer is off.
              </p>
              {created.template.force_offer_url && (
                <KeyRow label="Force offer (off)" value={created.template.force_offer_url} />
              )}
              {created.template.warning && (
                <p className="mt-1 text-xs text-warning">
                  App created, template deployed partially: {created.template.warning}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
